import { useState, useEffect } from 'react';
import { ArrowLeft, Clock, BookOpen, ChevronRight, ArrowRight } from 'lucide-react';

const siteUrl = () => window.themeData?.siteUrl ?? '';
const apiBase = () => (window.themeData?.apiUrl ?? '').replace(/\/$/, '') + '/little-anvi/v1';

const CAT_COLORS = [
  'bg-primary/10 text-primary',
  'bg-accent1/10 text-accent1',
  'bg-accent2/10 text-accent2',
  'bg-accent3/10 text-accent3',
];
const catColor = (slug = '') => CAT_COLORS[slug.length % 4];

function RelatedCard({ post }) {
  const cat   = post.categories?.[0];
  const color = catColor(cat?.slug ?? '');
  return (
    <a href={post.permalink} className="bg-white rounded-[24px] overflow-hidden shadow-card border border-gray-100 hover:shadow-soft hover:-translate-y-1 transition-all duration-300 group block">
      <div className="h-48 overflow-hidden">
        {post.featuredImage
          ? <img src={post.featuredImage.src} alt={post.featuredImage.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent1/10 flex items-center justify-center"><BookOpen className="w-10 h-10 text-primary/30" /></div>}
      </div>
      <div className="p-5">
        {cat && <span className={`text-xs font-black px-3 py-1 rounded-full mb-3 inline-block ${color}`}>{cat.name}</span>}
        <h4 className="font-black text-textDark text-base leading-snug mb-2 italic">{post.title}</h4>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-textLight font-bold">{post.date}</span>
          <span className="text-primary font-black text-xs flex items-center gap-1">Read <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </a>
  );
}

export default function BlogDetailPage({ postId }) {
  const [post,    setPost]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  useEffect(() => {
    if (!postId) { setError(true); setLoading(false); return; }
    fetch(`${apiBase()}/posts/${postId}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setPost)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [postId]);

  if (loading) return (
    <div className="pb-20 animate-pulse">
      <div className="h-72 bg-gradient-to-br from-primary/10 via-accent1/10 to-secondary/20 w-full" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 space-y-6">
        <div className="h-80 bg-gray-100 rounded-[28px]" />
        <div className="h-40 bg-gray-100 rounded-[28px]" />
      </div>
    </div>
  );

  if (error || !post) return (
    <div className="py-20 text-center">
      <BookOpen className="w-16 h-16 text-primary/30 mx-auto mb-4" />
      <h2 className="text-2xl font-black text-textDark mb-4 italic">Post not found</h2>
      <a href={`${siteUrl()}/blog/`} className="text-primary font-bold hover:text-primaryHover transition-colors">← Back to Blog</a>
    </div>
  );

  return (
    <div className="pb-20">

      {/* ── Hero ── */}
      <section className="w-full bg-gradient-to-br from-primary/10 via-accent1/10 to-secondary/20 py-14 md:py-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent1/10 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-5 flex-wrap text-sm font-bold">
            <a href={`${siteUrl()}/`} className="text-textLight hover:text-primary transition-colors">Home</a>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <a href={`${siteUrl()}/blog/`} className="text-textLight hover:text-primary transition-colors">Blog</a>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <span className="text-primary">Article</span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-5">
            {post.featured && <span className="bg-primary/10 text-primary text-xs font-black px-3 py-1 rounded-full">⭐ Featured</span>}
            {post.categories.map((c) => (
              <a key={c.slug} href={`${siteUrl()}/category/${c.slug}/`}
                 className={`text-xs font-black px-3 py-1 rounded-full hover:opacity-80 transition-opacity ${catColor(c.slug)}`}>
                {c.name}
              </a>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-textDark italic leading-tight max-w-4xl mb-5">
            {post.title}
          </h1>

          {/* Author + meta */}
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex items-center gap-3">
              <img src={post.author.avatarUrl} alt={post.author.name}
                   className="w-11 h-11 rounded-full object-cover border-2 border-primary/20" />
              <div>
                <p className="text-sm font-black text-textDark">{post.author.name}</p>
                <p className="text-xs text-textLight font-bold">{post.date}</p>
              </div>
            </div>
            <span className="flex items-center gap-1 text-sm text-textLight font-bold">
              <Clock className="w-4 h-4 text-primary" /> {post.readTime} min read
            </span>
          </div>
        </div>
      </section>

      {/* ── Article + Sidebar ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Article */}
          <article className="flex-1 min-w-0">
            {/* Featured image */}
            {post.featuredImage && (
              <div className="rounded-[28px] overflow-hidden mb-10 shadow-soft h-72 md:h-96">
                <img src={post.featuredImage.src} alt={post.featuredImage.alt} className="w-full h-full object-cover" />
              </div>
            )}

            {/* Post content — styled via .anvi-blog-content CSS class */}
            <div className="bg-white rounded-[28px] p-8 md:p-10 shadow-card border border-gray-100 anvi-blog-content"
                 dangerouslySetInnerHTML={{ __html: post.content }} />

            {/* Author card */}
            <div className="bg-white rounded-[28px] p-7 shadow-card border border-gray-100 mt-8 flex flex-col sm:flex-row gap-5 items-center sm:items-start">
              <img src={post.author.avatarUrl} alt={post.author.name}
                   className="w-20 h-20 rounded-2xl object-cover border-4 border-primary/20 flex-shrink-0" />
              <div className="text-center sm:text-left">
                <p className="text-xs font-black text-primary uppercase tracking-widest mb-1">Written by</p>
                <h4 className="text-lg font-black text-textDark mb-2">{post.author.name}</h4>
                <p className="text-textLight font-bold text-sm leading-relaxed">
                  {post.author.bio || 'Author at Little Anvi'}
                </p>
              </div>
            </div>
          </article>

          {/* Sticky sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28 space-y-6">
              <a href={`${siteUrl()}/blog/`}
                 className="flex items-center gap-2 text-primary hover:text-primaryHover font-bold text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" /> All Articles
              </a>
              {post.categories.length > 0 && (
                <div className="bg-white rounded-[24px] p-6 shadow-card border border-gray-100">
                  <h4 className="font-black text-textDark mb-4 italic">Categories</h4>
                  <div className="flex flex-col gap-2">
                    {post.categories.map((c) => (
                      <a key={c.slug} href={`${siteUrl()}/category/${c.slug}/`}
                         className={`text-sm font-black px-3 py-2 rounded-xl hover:opacity-80 transition-opacity block ${catColor(c.slug)}`}>
                        {c.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* ── Related posts ── */}
      {post.relatedPosts?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <h3 className="text-2xl font-black text-textDark italic mb-8">More <span className="text-primary">Articles</span></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {post.relatedPosts.map(p => <RelatedCard key={p.id} post={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
